import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { GlobalStyles } from './styles/GlobalStyles';
import { theme } from './styles/theme';
import { AppProvider } from './context/AppContext';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import Sidebar from './components/Sidebar';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import ProjectAnalysisDetailsPage from './pages/ProjectAnalysisDetailsPage';
import TenderBankPage from './pages/TenderBankPage';
import TenderDetailPage from './pages/TenderDetailPage';
import AnalysisPage from './pages/AnalysisPage';
import EvaluationPage from './pages/EvaluationPage';
import ResultsPage from './pages/ResultsPage';
import IssuerEvaluationFlow from './pages/IssuerEvaluationFlow';
import ResponseScoringFlow from './pages/ResponseScoringFlow';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import ContactPage from './pages/ContactPage';
import NotFoundPage from './pages/NotFoundPage';
import PilotPage from './pages/PilotPage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import KnowledgeBaseSetupPage from './pages/KnowledgeBaseSetupPage';
import ProtectedRoute from './components/ProtectedRoute';
import AdminDashboard from './components/AdminDashboard';
import styled from 'styled-components';

/**
 * React Router preserves scroll position across navigations, so following a
 * link from the foot of one page lands you at the foot of the next. Reset on
 * every path change.
 */
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);
  return null;
};

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
`;

const MainContent = styled.main`
  flex: 1;
  /* CRITICAL: without min-width: 0, this flex child defaults to
     min-width: auto and refuses to shrink below its intrinsic content
     width. Any descendant with a wide unbreakable token (long URL,
     hardcoded pixel width, etc.) pushes the entire main column past
     the viewport edge — every page-level horizontal-overflow bug
     traces back to this. */
  min-width: 0;
  margin-left: var(--sidebar-width, 280px);
  display: flex;
  flex-direction: column;
  transition: margin-left ${theme.transitions.base};

  @media (max-width: ${theme.breakpoints.md}) {
    margin-left: 0;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: ${theme.spacing[6]} ${theme.spacing[8]};

  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing[4]} ${theme.spacing[6]};
    margin-top: 80px; // Account for mobile toggle button
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing[4]};
  }
`;

function App() {
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <AuthProvider>
        <AppProvider>
          <ToastProvider>
            <Router>
            <ScrollToTop />
            <Routes>
              {/* Public routes without sidebar */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/auth/verify" element={<LoginPage />} />
              <Route path="/pilot" element={<PilotPage />} />
              
              {/* Protected routes with sidebar */}
              <Route path="/*" element={
                <ProtectedRoute>
                  <AppContainer>
                    <Sidebar />
                    <MainContent>
                      <ContentArea>
                        <Routes>
                          <Route path="/" element={<HomePage />} />
                          <Route path="/projects" element={<ProjectsPage />} />
                          <Route path="/projects/:id" element={<ProjectDetailPage />} />
                          <Route path="/projects/:projectId/analysis/:threadId" element={<ProjectAnalysisDetailsPage />} />
                          <Route path="/tender-bank" element={<TenderBankPage />} />
                          <Route path="/tender-bank/:id" element={<TenderDetailPage />} />
                          <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
                          <Route path="/knowledge-base/setup" element={<KnowledgeBaseSetupPage />} />
                          <Route path="/tender-response/:threadId?" element={<AnalysisPage />} />
                          {/* Redirect old /analysis route to new /tender-response */}
                          <Route path="/analysis/:threadId?" element={<Navigate to="/tender-response" replace />} />
                          <Route path="/evaluation" element={<EvaluationPage />} />
                          <Route path="/evaluation/issuer-flow" element={<IssuerEvaluationFlow />} />
                          <Route path="/evaluation/response-scoring" element={<ResponseScoringFlow />} />
                          <Route path="/results/:threadId" element={<ResultsPage />} />
                          <Route path="/profile" element={<ProfilePage />} />
                          <Route path="/settings" element={<SettingsPage />} />
                          <Route path="/contact" element={<ContactPage />} />
                          
                          {/* Admin-only routes */}
                          <Route path="/admin" element={
                            <ProtectedRoute adminOnly>
                              <AdminDashboard />
                            </ProtectedRoute>
                          } />
                          
                          <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                      </ContentArea>
                      <Footer />
                    </MainContent>
                  </AppContainer>
                </ProtectedRoute>
              } />
            </Routes>
            </Router>
          </ToastProvider>
        </AppProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
